import Phaser from 'phaser';
import { globals } from './globals';
import FingerprintGenerator from './components/FingerprintGenerator';
import AppData from './components/AppData';
import SoundManager from './components/SoundManager';
import BaseScene from './scenes/BaseScene';

export default class Bootloader extends Phaser.Scene {

    private resizeTimeout: number | null = null;
    public scenesRunning: string[] = [];
    public globals: any = globals;
    public AppData: AppData;
    public soundManager: SoundManager;
    
    constructor() {
        super('Bootloader'); 

        this.AppData = AppData.createEmpty();
        this.soundManager = SoundManager.getInstance(this);

        this.startApplication();
    }

    async startApplication() {

        // Init AppData
        await this.initAppData();
        this.AppData.save();
   
        // Refresh Scores from Remote
        this.AppData.refreshScores();

        // Init Sound Manager
        this.soundManager.setVolume(this.AppData.settings.soundVolume);
    }

    async initAppData(): Promise<void> {

        // Get Fingerprint
        const fingerprint = await FingerprintGenerator.generate();

        // Set Fingerprint
        this.AppData.user.fingerprint = fingerprint;

        // Get AppData from Local if exist
        let appData = AppData.fromLocalStorage();
        if(appData) {
            this.AppData = appData;
            return;
        }

        // Get AppData from External if exist
        appData = await AppData.fromExternalStorage(fingerprint);
        if(appData) {
            this.AppData = appData;
            return;
        }
    }

    public preload(): void {
        // Preload images
        this.load.image('preload_bg', 'assets/images/preload_bg.jpg');
        this.load.spritesheet('button_preload_start', 'assets/images/button_preload_start.png', {frameWidth: 294, frameHeight: 97});
    }

    public create(): void {
        this.cameras.main.setBackgroundColor('#2c3e50');

        this.scale.on('resize', this.handleResize, this);
        
        this.launchScene('Preload');
    }

    public handleResize(): void {

        // Clean Timer
        if (this.resizeTimeout !== null) {
            clearTimeout(this.resizeTimeout);
        }

        // Set Timer
        this.resizeTimeout = window.setTimeout(() => {
 
            // Restart active scenes
            this.scenesRunning.forEach((sceneRunning) => {
                const scene = this.scene.get(sceneRunning) as BaseScene;
                scene.cleanUp();
                scene.restart();
            });
            this.scenesRunning = [];

            this.resizeTimeout = null;
        }, 600);
    }

    public launchScene(scene: string, data?: any): void {
        this.scenesRunning.forEach((sceneRunning) => {
            const runningScene = this.scene.get(sceneRunning) as BaseScene;
            runningScene.cleanUp();
            this.scene.stop(sceneRunning);
        });

        this.scenesRunning = [];
        this.scene.launch(scene, data);
    }

    public stopScene(scene: string): void {
        this.scene.stop(scene);

        const index = this.scenesRunning.indexOf(scene);
        if (index > -1) {
            this.scenesRunning.splice(index, 1);
        }
    }

    public completeLevel(levelId: number, score: number): void {
        this.AppData.completeLevel(levelId, score);
    }

    public setSoundVolume(volume: number): void {
        this.AppData.setSoundVolume(volume);
    }

    public setGameSpeed(speed: number): void {
        this.AppData.setGameSpped(speed);
    }

    public setUserName(name: string): void {
        this.AppData.setUserName(name);
    }


    updateResize(scene: BaseScene): void {
        const screenWidth = scene.scale.gameSize.width;
        const screenHeight = scene.scale.gameSize.height;
        const gameWidth = scene.width;
        const gameHeight = scene.height;

        const parentSize = new Phaser.Structs.Size(screenWidth, screenHeight);
        const sizer = new Phaser.Structs.Size(gameWidth, gameHeight, Phaser.Structs.Size.FIT, parentSize);

        parentSize.setSize(screenWidth, screenHeight);
        sizer.setSize(screenWidth, screenHeight);

        this.updateCamera(scene, sizer.width, sizer.height);
    }

    updateCamera(scene: BaseScene, width: number, height: number): void {
        const camera = scene.cameras.main;
        const scaleX = width / scene.width;
        const scaleY = height / scene.height;

        camera.setZoom(Math.max(scaleX, scaleY));
        camera.centerOn((scene.width as number) / 2, (scene.height as number) / 2);
    }
}